import { type ReactNode, useState } from 'react';
import type { FieldMetadata } from '../../../data/client';
import { useDeleteRecord } from '../../../data/hooks';
import { useRelationLabel } from '../../../fields/useRelationLabel';
import {
  Button,
  ContextMenu,
  ContextMenuItem,
  Dialog,
  DropdownItem,
  DropdownMenu,
  IconButton,
  useToast,
} from '../../../ui';

/**
 * REMOVING A MEMBERSHIP (D5), and the copy is the feature.
 *
 * "Remove" next to a person's name is ambiguous in the one direction that matters: it
 * could plausibly mean deleting the person. The confirmation therefore says what it
 * does AND what it leaves alone, because a user who hesitates here is right to, and the
 * product should answer the question rather than let them find out.
 *
 * Under the hood it is an ordinary soft delete of the junction record. Re-adding the
 * same pair later RESTORES this tombstone rather than creating a second membership,
 * which is why a returning member comes back with their history intact.
 *
 * THE NAME COMES FROM THE SAME PLACE THE VISIBLE ONE DOES. `label` is the label the
 * LIST query already resolved (D3); without it this falls back to `useRelationLabel`,
 * which is exactly what the row's own link falls back to - so the menu item and the name
 * beside it can never disagree, and the fallback costs no request the row was not
 * already making.
 *
 * WAVE 4 (C13): IT DRAWS NOTHING AT REST. Every junction row carried a close glyph
 * labelled "Remove <name>" as its trailing accessory, so a list of eight members was eight
 * destructive controls standing open on a screen whose other rows have none - and the
 * readiness inventory names it as the one row in the product that still does this. Apple's
 * list row keeps a row's actions in the context menu, which the secondary click and the
 * touch-and-hold both reach, and shows a More under the pointer. The confirmation is
 * unchanged: it is the part that carries the copy the decision is about.
 * hig: https://developer.apple.com/design/human-interface-guidelines/context-menus
 * kit: Apple macOS 27 UI Kit.sketch / Lists/Light/Rows/Default
 */
export interface UnlinkActionProps {
  readonly junctionObjectId: string;
  readonly junctionRecordId: string;
  /** The endpoint naming the other side, for the fallback resolution. */
  readonly endpointField: FieldMetadata | undefined;
  readonly endpointRecordId: string | null;
  /** The label the query resolved, when it did. */
  readonly label: string | undefined;
  /** The row this acts on: it becomes the context menu's trigger. */
  readonly children: ReactNode;
}

export function UnlinkAction({
  junctionObjectId,
  junctionRecordId,
  endpointField,
  endpointRecordId,
  label,
  children,
}: UnlinkActionProps) {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const remove = useDeleteRecord(junctionObjectId);
  const resolved = useRelationLabel(endpointField, label === undefined ? endpointRecordId : null);
  const memberLabel = label ?? resolved.label;

  // Never a uuid: a confirmation must not ask somebody to recognise an id.
  const name = memberLabel ?? 'this member';

  return (
    <>
      <ContextMenu
        trigger={
          <div className="record-row-hover">
            {children}
            {/* THE ROW'S ONE TRAILING CONTROL, and only under the pointer or the keyboard.
                A coarse pointer has no hover, so `.record-row-hover` draws it there - the
                same pair the comment row states. */}
            <span className="record-row-more">
              <DropdownMenu
                trigger={
                  <IconButton icon="more-horizontal" label={`Actions for ${name}`} size="sm" />
                }
                align="end"
              >
                <DropdownItem icon="trash" tone="danger" onSelect={() => setOpen(true)}>
                  {`Remove ${name}`}
                </DropdownItem>
              </DropdownMenu>
            </span>
          </div>
        }
      >
        <ContextMenuItem icon="trash" tone="danger" onSelect={() => setOpen(true)}>
          {`Remove ${name}`}
        </ContextMenuItem>
      </ContextMenu>
      <Dialog
        alert
        open={open}
        onOpenChange={setOpen}
        title="Remove this member?"
        description={`${name} stops being a member here. The record itself stays, with everything else on it.`}
        width="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() =>
                remove.mutate(junctionRecordId, {
                  onSuccess: () => {
                    setOpen(false);
                    toast({ title: `${name} was removed`, tone: 'neutral' });
                  },
                  onError: (error) =>
                    toast({
                      title: 'Could not remove this member',
                      description: error.message,
                      tone: 'danger',
                    }),
                })
              }
            >
              Remove
            </Button>
          </div>
        }
      >
        {null}
      </Dialog>
    </>
  );
}
